import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Send, User, CheckCircle, ArrowLeft, Reply, MoreVertical, Trash2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api/api';

const DiscussionForum = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contentId = searchParams.get('content_id');
  const assignmentId = searchParams.get('assignment_id');
  
  const [discussions, setDiscussions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState('all');
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterCourse, contentId, assignmentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (contentId) params.content_id = contentId;
      if (assignmentId) params.assignment_id = assignmentId;
      if (filterCourse !== 'all') params.course_id = filterCourse;
      
      const [discussionsRes, coursesRes] = await Promise.all([
        api.get('/lms/discussions', { params }),
        api.get('/lms/teacher/courses')
      ]);
      
      setDiscussions(discussionsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setPosting(true);
    try {
      const courseId = filterCourse !== 'all' ? filterCourse : (courses[0]?.id || '');
      await api.post('/lms/discussions', {
        message: newMessage,
        course_id: courseId,
        content_id: contentId || null,
        assignment_id: assignmentId || null
      });
      
      toast.success('Question posted successfully');
      setNewMessage('');
      fetchData();
    } catch (error) {
      console.error('Error posting:', error);
      toast.error('Failed to post question');
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (discussionId) => {
    if (!replyMessage.trim()) return;
    
    setPosting(true);
    try {
      const discussion = discussions.find(d => d.id === discussionId);
      await api.post('/lms/discussions', {
        message: replyMessage,
        course_id: discussion?.course_id || '',
        content_id: discussion?.content_id || null,
        assignment_id: discussion?.assignment_id || null,
        parent_id: discussionId
      });
      
      toast.success('Reply posted');
      setReplyMessage('');
      setReplyingTo(null);
      fetchData();
    } catch (error) {
      console.error('Error replying:', error);
      toast.error('Failed to post reply');
    } finally {
      setPosting(false);
    }
  };

  const handleMarkAnswered = async (discussionId) => {
    try {
      await api.post(`/lms/discussions/${discussionId}/answer`);
      toast.success('Marked as answered');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as answered');
    }
  };

  const handleDelete = async (discussionId) => {
    if (!window.confirm('Delete this discussion?')) return;
    
    try {
      await api.delete(`/lms/discussions/${discussionId}`);
      toast.success('Discussion deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6" data-testid="lms-discussion-forum">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/lms')} size="sm">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Discussion Forum</h1>
            <p className="text-gray-600">Ask questions and interact with teachers</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filter by Course</label>
              <Select value={filterCourse} onValueChange={setFilterCourse}>
                <SelectTrigger>
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* New Question Form */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="text-blue-600" size={20} />
            Ask a Question
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePost} className="flex gap-3">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your question here..."
              className="flex-1"
              rows={2}
            />
            <Button 
              type="submit" 
              disabled={posting || !newMessage.trim()}
              className="bg-blue-600 hover:bg-blue-700 self-end"
            >
              <Send size={18} className="mr-1" />
              Post
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Discussions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : discussions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No discussions yet. Be the first to ask a question!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => (
            <Card key={discussion.id} className={`${discussion.is_answered ? 'border-green-300 bg-green-50/30' : ''}`} data-testid={`discussion-${discussion.id}`}>
              <CardContent className="p-4">
                {/* Main Question */}
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-800">{discussion.user_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${discussion.user_role === 'Student' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {discussion.user_role}
                      </span>
                      {discussion.is_answered && (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-1">
                          <CheckCircle size={12} /> Answered
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{formatDate(discussion.created_at)}</span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{discussion.message}</p>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-3">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setReplyingTo(replyingTo === discussion.id ? null : discussion.id)}
                        className="text-gray-500 hover:text-blue-600"
                      >
                        <Reply size={14} className="mr-1" />
                        Reply ({discussion.replies_count || 0})
                      </Button>
                      {!discussion.is_answered && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleMarkAnswered(discussion.id)}
                          className="text-gray-500 hover:text-green-600"
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Mark Answered
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(discussion.id)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Replies */}
                {discussion.replies && discussion.replies.length > 0 && (
                  <div className="ml-12 mt-4 space-y-3 border-l-2 border-gray-200 pl-4">
                    {discussion.replies.map((reply) => (
                      <div key={reply.id} className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${reply.user_role === 'Student' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                          <User className={reply.user_role === 'Student' ? 'text-blue-600' : 'text-purple-600'} size={16} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-gray-800">{reply.user_name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${reply.user_role === 'Student' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {reply.user_role}
                            </span>
                            <span className="text-xs text-gray-400">{formatDate(reply.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700">{reply.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Reply Form */}
                {replyingTo === discussion.id && (
                  <div className="ml-12 mt-4 flex gap-2">
                    <Input
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Write your reply..."
                      className="flex-1"
                    />
                    <Button 
                      onClick={() => handleReply(discussion.id)}
                      disabled={posting || !replyMessage.trim()}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Send size={14} />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscussionForum;
